import React from "react";
import { 
  Users, Briefcase, Building2, GraduationCap, School, LineChart, 
  Target, Sparkles, ArrowRight, Compass, CheckCircle2, ShieldCheck, 
  Search, BookOpen 
} from "lucide-react";
import { useSeoMetadata } from "./PublicPages";
import { SeoBreadcrumbs } from "./seo/SeoBreadcrumbs";
import { SeoRelatedLinks } from "./seo/SeoRelatedLinks";
import { GUIDE_REGISTRY } from "../utils/guideRegistry";

interface AudienceDirectoryPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
}

interface AudienceDirectoryCard {
  id: string;
  route: string;
  title: string;
  shortTitle: string;
  description: string;
  targetRole: string;
  icon: any;
  color: string;
  badge: string;
  featuredGuideSlugs: string[];
}

export function AudienceDirectoryPage({ onNavigate, onLogin = () => {} }: AudienceDirectoryPageProps) {
  useSeoMetadata({
    title: "Audience Directory: Solutions for Job Seekers, Recruiters & Sales | BizNearYou",
    description: "Explore BizNearYou solutions tailored for Job Seekers, Recruiters, Staffing Agencies, Career Coaches, Workforce Boards, Career Centers, Sales Teams, and Agencies.",
    canonicalUrl: "https://getbiznearyou.com/for",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "BizNearYou Audience Directory",
      "description": "Directory of solutions tailored by professional audience role and search intent.",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "For Job Seekers", "url": "https://getbiznearyou.com/for-job-seekers" },
        { "@type": "ListItem", "position": 2, "name": "For Recruiters", "url": "https://getbiznearyou.com/for-recruiters" },
        { "@type": "ListItem", "position": 3, "name": "For Staffing Agencies", "url": "https://getbiznearyou.com/for-staffing-agencies" },
        { "@type": "ListItem", "position": 4, "name": "For Career Coaches", "url": "https://getbiznearyou.com/for-career-coaches" },
        { "@type": "ListItem", "position": 5, "name": "For Workforce Organizations", "url": "https://getbiznearyou.com/for-workforce-organizations" },
        { "@type": "ListItem", "position": 6, "name": "For College Career Centers", "url": "https://getbiznearyou.com/for-college-career-centers" },
        { "@type": "ListItem", "position": 7, "name": "For Sales Teams", "url": "https://getbiznearyou.com/for-sales-teams" },
        { "@type": "ListItem", "position": 8, "name": "For Business Development", "url": "https://getbiznearyou.com/for-business-development" },
        { "@type": "ListItem", "position": 9, "name": "For Agencies & Freelancers", "url": "https://getbiznearyou.com/for-agencies" }
      ]
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  const audiences: AudienceDirectoryCard[] = [
    {
      id: "job_seeker",
      route: "/for-job-seekers",
      title: "For Job Seekers & Local Explorers",
      shortTitle: "Job Seekers",
      description: "Discover nearby independent employers, review commercial hiring signals, and contact business owners directly before job postings hit saturated job boards.",
      targetRole: "Job Seekers, Career Changers, Students",
      icon: Users,
      color: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800",
      badge: "Individual Seeker",
      featuredGuideSlugs: [
        "how-to-ask-if-a-business-is-hiring",
        "how-to-find-hidden-jobs",
        "how-to-email-a-business-about-a-job"
      ]
    },
    {
      id: "recruiter",
      route: "/for-recruiters",
      title: "For Headhunters & Third-Party Recruiters",
      shortTitle: "Recruiters",
      description: "Identify growing local companies showing active hiring demand signals. Build targeted search prospect lists and pitch search agreements before HR advertises roles.",
      targetRole: "Independent Recruiters & Search Consultants",
      icon: Briefcase,
      color: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-800",
      badge: "B2B Talent Acquisition",
      featuredGuideSlugs: [
        "recruitment-lead-generation",
        "how-to-find-recruiting-clients",
        "hiring-intent-signals"
      ]
    },
    {
      id: "staffing_agency",
      route: "/for-staffing-agencies",
      title: "For Staffing Agencies & Workforce Supply",
      shortTitle: "Staffing Agencies",
      description: "Locate businesses facing customer volume surges or seasonal spikes. Offer rapid temp, temp-to-hire, and high-volume workforce supply solutions.",
      targetRole: "Staffing Agency Owners, Business Developers",
      icon: Building2,
      color: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800",
      badge: "High-Volume Workforce",
      featuredGuideSlugs: [
        "staffing-agency-lead-generation",
        "how-to-get-clients-for-a-staffing-agency",
        "how-to-find-businesses-that-need-staffing"
      ]
    },
    {
      id: "career_coach",
      route: "/for-career-coaches",
      title: "For Career Coaches & Private Advisors",
      shortTitle: "Career Coaches",
      description: "Curate custom employer target lists for coaching clients. Track client outreach in shared pipelines and uncover unadvertised local opportunities.",
      targetRole: "Executive Coaches, Resume Writers, Career Counselors",
      icon: GraduationCap,
      color: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800",
      badge: "Client Advisory",
      featuredGuideSlugs: [
        "job-search-tools-for-career-coaches",
        "how-to-help-clients-find-hidden-jobs",
        "how-to-build-an-employer-target-list"
      ]
    },
    {
      id: "workforce_org",
      route: "/for-workforce-organizations",
      title: "For Workforce Development & Non-Profits",
      shortTitle: "Workforce Orgs",
      description: "Map regional employment demand across trade, health, and commercial sectors. Partner with local employers to place community job seekers efficiently.",
      targetRole: "Workforce Boards, Non-Profit Directors, Case Managers",
      icon: School,
      color: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800",
      badge: "Community & Regional",
      featuredGuideSlugs: [
        "employer-sourcing-for-workforce-programs",
        "how-workforce-programs-find-local-employers",
        "how-to-build-an-employer-target-list"
      ]
    },
    {
      id: "college_career_center",
      route: "/for-college-career-centers",
      title: "For College & University Career Centers",
      shortTitle: "Career Centers",
      description: "Connect students and alumni with local independent employers near campus. Discover internship hosts and entry-level graduate hiring partners.",
      targetRole: "University Career Directors, Employer Relations Managers",
      icon: GraduationCap,
      color: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/60 dark:text-teal-300 dark:border-teal-800",
      badge: "Higher Education",
      featuredGuideSlugs: [
        "how-career-centers-find-employers",
        "how-to-find-internship-employers",
        "how-to-find-an-internship-near-you"
      ]
    },
    {
      id: "sales_professional",
      route: "/for-sales-teams",
      title: "For B2B Sales Teams & Account Executives",
      shortTitle: "Sales Teams",
      description: "Prospect local commercial businesses using footprint changes, review velocity, and business growth triggers. Export high-density B2B prospect lists.",
      targetRole: "Account Executives, BDRs, Sales Managers",
      icon: LineChart,
      color: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800",
      badge: "B2B Commercial Sales",
      featuredGuideSlugs: [
        "how-to-find-growing-businesses",
        "how-to-find-newly-opened-businesses",
        "business-growth-signals"
      ]
    },
    {
      id: "business_development",
      route: "/for-business-development",
      title: "For Business Development & Partnerships",
      shortTitle: "Business Development",
      description: "Identify fast-growing local companies, multi-location expansion targets, and commercial partnership opportunities across regional metro areas.",
      targetRole: "BizDev Directors, Regional Partnership Leads",
      icon: Target,
      color: "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/60 dark:text-cyan-300 dark:border-cyan-800",
      badge: "Strategic Partnerships",
      featuredGuideSlugs: [
        "how-to-find-growing-businesses",
        "business-growth-signals",
        "how-to-build-a-local-business-prospect-list"
      ]
    },
    {
      id: "agency_freelancer",
      route: "/for-agencies",
      title: "For Marketing Agencies & B2B Consultants",
      shortTitle: "Agencies & Freelancers",
      description: "Audit local small businesses for website gaps, missing SEO structure, and digital contact channels. Pitch web, marketing, and operational services.",
      targetRole: "Agency Owners, Digital Marketers, Freelancers",
      icon: Sparkles,
      color: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/60 dark:text-violet-300 dark:border-violet-800",
      badge: "Agency Prospecting",
      featuredGuideSlugs: [
        "how-agencies-find-local-clients",
        "business-growth-signals",
        "how-to-find-newly-opened-businesses"
      ]
    }
  ];

  return (
    <div className="w-full bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 min-h-screen">
      <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        <SeoBreadcrumbs items={[{ label: "Audience Directory", path: "/for" }]} onNavigate={onNavigate} />

        {/* Hero Banner Header */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 space-y-4 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              <Compass className="w-3.5 h-3.5" /> Professional & Job Seeker Directory
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              Solutions Tailored for Every Professional Search Intent
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              BizNearYou dynamically adapts local commercial business intelligence for 9 specialized user groups—from job seekers discovering unposted openings to recruiters, staffing agencies, and sales teams prospecting local employer markets.
            </p>
          </div>
        </section>

        {/* Audience Grid Section */}
        <section className="space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-slate-950 dark:text-white tracking-tight">
                Select Your Audience Role
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                Each audience mode re-indexes commercial business signals around your specific goals.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audiences.map((aud) => {
              const IconComp = aud.icon;
              return (
                <div
                  key={aud.id}
                  className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-xs hover:shadow-md transition flex flex-col justify-between space-y-6"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className={`p-2.5 rounded-xl border ${aud.color} flex items-center gap-2`}>
                        <IconComp className="w-5 h-5" />
                        <span className="text-xs font-black uppercase tracking-wider">{aud.shortTitle}</span>
                      </div>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                        {aud.badge}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-snug">
                      <a
                        href={aud.route}
                        onClick={(e) => handleLinkClick(e, aud.route)}
                        className="hover:text-indigo-600 dark:hover:text-indigo-400 transition cursor-pointer"
                      >
                        {aud.title}
                      </a>
                    </h3>

                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {aud.description}
                    </p>

                    <div className="pt-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 block mb-1">
                        Primary Target Users:
                      </span>
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60 inline-block">
                        {aud.targetRole}
                      </span>
                    </div>

                    {/* Featured Guides for this Audience */}
                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        Featured Guides for {aud.shortTitle}:
                      </span>
                      <div className="flex flex-col gap-1.5">
                        {aud.featuredGuideSlugs.map((slug) => {
                          const guide = GUIDE_REGISTRY[slug];
                          if (!guide) return null;
                          return (
                            <a
                              key={slug}
                              href={`/guides/${slug}`}
                              onClick={(e) => handleLinkClick(e, `/guides/${slug}`)}
                              className="text-xs text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium truncate flex items-center gap-1.5 group cursor-pointer"
                            >
                              <span className="w-1 h-1 rounded-full bg-indigo-500 flex-shrink-0" />
                              <span className="truncate group-hover:underline">{guide.h1}</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <a
                      href={aud.route}
                      onClick={(e) => handleLinkClick(e, aud.route)}
                      className="w-full py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                    >
                      <span>Explore {aud.shortTitle} Solution</span>
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Action CTA Banner */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 text-center space-y-4 shadow-sm">
          <div className="p-3 bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 w-fit mx-auto rounded-2xl border border-indigo-200 dark:border-indigo-800">
            <Sparkles className="w-6 h-6" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 dark:text-white tracking-tight">
            Ready to Accelerate Your Local Sourcing Workflow?
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Search nearby businesses, inspect real-time commercial activity signals, and export targeted employer lists with BizNearYou.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-3">
            <a
              href="/find-jobs"
              onClick={(e) => handleLinkClick(e, "/find-jobs")}
              className="px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs transition inline-flex items-center gap-2 cursor-pointer shadow-sm"
            >
              <span>Search Employers Near You</span>
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="/guides"
              onClick={(e) => handleLinkClick(e, "/guides")}
              className="px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold text-xs transition inline-flex items-center gap-2 cursor-pointer border border-slate-200 dark:border-slate-700"
            >
              <span>Browse All Guides</span>
            </a>
          </div>
        </section>

        <SeoRelatedLinks currentRoute="/for" onNavigate={onNavigate} />
      </main>
    </div>
  );
}
